/**
 * Derived quantities. Everything here is a pure function of `PlayerState`.
 *
 * Keeping these out of the state object means a save file can never disagree
 * with itself: net worth is always recomputed, never stored and forgotten.
 */

import type {
  AssetClassId,
  LifeStage,
  Person,
  PersonKind,
  PlayerState,
} from './types';
import { HABIT_KEYS, LIFE_STAGES, SKILL_KEYS, TRAIT_KEYS } from './types';

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

export const clamp100 = (v: number): number => clamp(v, 0, 100);

export const round = (v: number, dp = 0): number => {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

/** Smooth 0→1 ramp between `a` and `b`. */
export function ramp(v: number, a: number, b: number): number {
  if (b === a) return v >= b ? 1 : 0;
  return clamp((v - a) / (b - a), 0, 1);
}

/* ------------------------------------------------------------------ *
 * Stages
 * ------------------------------------------------------------------ */

const STAGE_BOUNDS: readonly (readonly [LifeStage, number])[] = [
  ['launch', 22],
  ['earlyCareer', 28],
  ['establishing', 35],
  ['growth', 45],
  ['midlife', 55],
  ['lateCareer', 64],
  ['preRetirement', 68],
  ['retirement', 80],
  ['lateLife', Number.POSITIVE_INFINITY],
];

export function stageForAge(age: number): LifeStage {
  for (const [stage, upper] of STAGE_BOUNDS) {
    if (age < upper) return stage;
  }
  return 'lateLife';
}

export function stageIndex(stage: LifeStage): number {
  return LIFE_STAGES.indexOf(stage);
}

export const STAGE_LABEL: Record<LifeStage, string> = {
  launch: 'Launch',
  earlyCareer: 'Early career',
  establishing: 'Establishing',
  growth: 'Growth',
  midlife: 'Midlife',
  lateCareer: 'Late career',
  preRetirement: 'Pre-retirement',
  retirement: 'Retirement',
  lateLife: 'Late life',
};

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

/** Annual discretionary spending implied by the lifestyle tier. */
export function lifestyleCost(level: number): number {
  const tier = clamp(Math.round(level), 0, 10);
  // Roughly geometric: each tier costs ~34% more than the last. Tier 0 is
  // genuine austerity; tier 10 is the spending of someone who earns a lot and
  // has stopped noticing.
  return Math.round(9500 * 1.42 ** tier);
}

/**
 * The lifestyle tier a given income tends to settle at. Lifestyle inflation is
 * not a moral failing in this model, it is the default: without deliberate
 * effort, spending rises to meet income.
 */
export function lifestyleTargetForIncome(disposable: number): number {
  if (disposable < 17000) return 0;
  if (disposable < 25000) return 1;
  if (disposable < 35000) return 2;
  if (disposable < 48000) return 3;
  if (disposable < 65000) return 4;
  if (disposable < 88000) return 5;
  if (disposable < 120000) return 6;
  if (disposable < 170000) return 7;
  if (disposable < 250000) return 8;
  if (disposable < 380000) return 9;
  return 10;
}

/**
 * Progressive income tax. A game abstraction, but an essential one: without it
 * high earners accumulate implausible wealth and the economy stops producing a
 * believable spread of outcomes.
 */
export function annualTax(state: PlayerState): number {
  const gross = grossIncome(state);
  if (gross <= 0) return 0;
  const bands: readonly (readonly [number, number])[] = [
    [14000, 0],
    [48000, 0.19],
    [125000, 0.31],
    [260000, 0.39],
    [Number.POSITIVE_INFINITY, 0.46],
  ];
  let tax = 0;
  let previous = 0;
  for (const [ceiling, rate] of bands) {
    if (gross <= previous) break;
    const slice = Math.min(gross, ceiling) - previous;
    tax += slice * rate;
    previous = ceiling;
  }
  // Dependents reduce the effective rate a little.
  const relief = Math.min(0.12, state.family.dependents * 0.035);
  return Math.round(tax * (1 - relief));
}

/** Income actually available to spend or save. */
export function disposableIncome(state: PlayerState): number {
  return Math.max(0, grossIncome(state) - annualTax(state));
}

/** Happiness contribution of a lifestyle tier, with strong diminishing returns. */
export function lifestyleComfort(level: number): number {
  const tier = clamp(level, 0, 10);
  return 26 * Math.log1p(tier) / Math.log1p(10);
}

export function childCost(person: Person): number {
  const age = person.age;
  if (age < 2) return 7800;
  if (age < 6) return 6400;
  if (age < 12) return 6900;
  if (age < 18) return 8600;
  if (age < 23) return 9400;
  return 0;
}

export function annualChildCosts(state: PlayerState): number {
  let total = 0;
  for (const p of state.people) {
    if (p.kind !== 'child' || !p.alive) continue;
    if (p.age > 23) continue;
    total += childCost(p);
  }
  // A partner shares the load.
  const shared = state.family.maritalStatus === 'married' || state.family.maritalStatus === 'partnered';
  return Math.round(total * (shared ? 0.62 : 1));
}

export function annualHealthCosts(state: PlayerState): number {
  let total = 0;
  for (const c of state.conditions) total += 220 + c.severity * 58;
  const ageLoad = Math.max(0, state.age - 45) * 42;
  const healthLoad = (100 - state.stats.health) * 14;
  return Math.round(total + ageLoad + healthLoad);
}

export function totalDebt(state: PlayerState): number {
  let total = 0;
  for (const d of state.finance.debts) total += d.principal;
  return Math.round(total);
}

/**
 * Debt excluding secured and subsidised borrowing. A mortgage is backed by a
 * house and a student loan by income-contingent terms; treating either as
 * grounds for insolvency made a third of all homeowners go bankrupt.
 */
export function unsecuredDebt(state: PlayerState): number {
  let total = 0;
  for (const d of state.finance.debts) {
    if (d.kind === 'mortgage' || d.kind === 'student') continue;
    total += d.principal;
  }
  return Math.round(total);
}

export function annualDebtService(state: PlayerState): number {
  let total = 0;
  for (const d of state.finance.debts) {
    total += Math.max(d.minPayment, d.principal * d.rate);
  }
  return Math.round(total);
}

export function investableValue(state: PlayerState): number {
  let total = 0;
  for (const key of Object.keys(state.finance.holdings) as AssetClassId[]) {
    if (key === 'property') continue;
    total += state.finance.holdings[key].value;
  }
  return Math.round(total);
}

/**
 * A live-in partner's contribution to the household.
 *
 * This is *derived* from the partner rather than added to `otherIncome` by an
 * event effect. The additive version silently stacked every time the proposal
 * event re-fired, which inflated household income by tens of thousands and
 * made every simulated life wealthy.
 */
export function partnerIncome(state: PlayerState): number {
  const status = state.family.maritalStatus;
  if (status !== 'married' && status !== 'partnered') return 0;
  const person = state.people.find((p) => p.alive && (p.kind === 'spouse' || p.kind === 'partner'));
  if (!person) return 0;
  const base = typeof person.meta['income'] === 'number' ? (person.meta['income'] as number) : 34000;
  // Partners scale back too, when there are small children at home.
  const youngKids = state.people.filter((p) => p.alive && p.kind === 'child' && p.age < 6).length;
  const retired = person.age >= 66;
  const factor = retired ? 0.42 : 1 - Math.min(0.45, youngKids * 0.22);
  return Math.round(base * factor);
}

/** Part-time earnings while enrolled, derived from remaining work capacity. */
export function studyIncome(state: PlayerState): number {
  const enrollment = state.education.enrollment;
  if (!enrollment) return 0;
  return Math.round(19000 * enrollment.workCapacity);
}

export function grossIncome(state: PlayerState): number {
  const { salary, otherIncome } = state.finance;
  let business = 0;
  if (state.business.active) {
    business = Math.max(0, state.business.revenue - state.business.operatingCost);
  }
  return Math.round(salary + otherIncome + business + partnerIncome(state) + studyIncome(state));
}

/**
 * Everything the player must pay for in a normal year, *excluding* debt
 * service. Debt is settled separately because payments are capped by ability
 * to pay — folding it in here would double-count it and let expenses grow
 * without bound.
 */
export function annualExpenses(state: PlayerState): number {
  return Math.round(
    state.finance.fixedExpenses +
      lifestyleCost(state.finance.lifestyleLevel) +
      annualChildCosts(state) +
      annualHealthCosts(state) +
      (state.education.enrollment?.annualCost ?? 0),
  );
}

/** What the player must find for expenses plus debt payments combined. */
export function annualOutgoings(state: PlayerState): number {
  return annualExpenses(state) + annualDebtService(state);
}

export function netCashFlow(state: PlayerState): number {
  return disposableIncome(state) - annualExpenses(state);
}

export function netWorth(state: PlayerState): number {
  // `investableValue` deliberately excludes the property holding so the home is
  // counted exactly once, from `finance.property`.
  const assets =
    state.finance.cash +
    investableValue(state) +
    state.finance.retirementAssets +
    (state.finance.property.owned ? state.finance.property.value : 0) +
    (state.business.active ? state.business.valuation : 0);
  return Math.round(assets - totalDebt(state));
}

/** How many months the player could survive on liquid assets alone. */
export function runwayMonths(state: PlayerState): number {
  const monthly = annualExpenses(state) / 12;
  if (monthly <= 0) return 999;
  const liquid = state.finance.cash + state.finance.holdings.conservative.value * 0.9;
  return round(liquid / monthly, 1);
}

export function debtPressure(state: PlayerState): number {
  const income = Math.max(12000, disposableIncome(state));
  return clamp100((totalDebt(state) / income) * 32);
}

export type DebtBand = 'none' | 'manageable' | 'elevated' | 'distressed' | 'crisis';

export function debtBand(state: PlayerState): DebtBand {
  const p = debtPressure(state);
  if (totalDebt(state) <= 0) return 'none';
  if (p < 25) return 'manageable';
  if (p < 50) return 'elevated';
  if (p < 75) return 'distressed';
  return 'crisis';
}

/* ------------------------------------------------------------------ *
 * People
 * ------------------------------------------------------------------ */

export function peopleOfKind(state: PlayerState, kind: PersonKind): Person[] {
  return state.people.filter((p) => p.kind === kind && p.alive);
}

export function spouse(state: PlayerState): Person | undefined {
  return state.people.find((p) => p.kind === 'spouse' && p.alive);
}

export function partner(state: PlayerState): Person | undefined {
  return state.people.find((p) => (p.kind === 'partner' || p.kind === 'spouse') && p.alive);
}

export function children(state: PlayerState): Person[] {
  return peopleOfKind(state, 'child');
}

export function bondScore(p: Person): number {
  const { trust, closeness, support, conflict } = p.bond;
  return clamp100((trust + closeness + support) / 3 - conflict * 0.45);
}

/** 0–100 aggregate quality of the player's whole social world. */
export function socialHealth(state: PlayerState): number {
  const living = state.people.filter((p) => p.alive && p.kind !== 'rival' && p.kind !== 'ex');
  if (living.length === 0) return 12;
  const weights: Partial<Record<PersonKind, number>> = {
    spouse: 3,
    partner: 2.4,
    child: 2,
    parent: 1.2,
    sibling: 1,
    friend: 1.1,
    mentor: 0.8,
    colleague: 0.5,
  };
  let sum = 0;
  let wsum = 0;
  for (const p of living) {
    const w = weights[p.kind] ?? 0.4;
    sum += bondScore(p) * w;
    wsum += w;
  }
  const base = wsum > 0 ? sum / wsum : 20;
  // Breadth matters, but with sharply diminishing returns.
  const breadth = Math.min(12, living.length) / 12;
  return clamp100(base * (0.82 + 0.18 * breadth));
}

/** 0–100 how much practical help the player can draw on in a crisis. */
export function supportNetwork(state: PlayerState): number {
  let best = 0;
  let total = 0;
  for (const p of state.people) {
    if (!p.alive) continue;
    const s = p.bond.support * (p.kind === 'spouse' ? 1.15 : 1);
    best = Math.max(best, s);
    total += s;
  }
  return clamp100(best * 0.6 + Math.min(40, total / 6));
}

/* ------------------------------------------------------------------ *
 * Capacity
 * ------------------------------------------------------------------ */

/**
 * Fraction of a full working year the player can actually deliver, given
 * health, energy, burnout, family load and study commitments.
 */
export function workCapacity(state: PlayerState): number {
  const { health, energy } = state.stats;
  let cap = 0.45 + 0.3 * (health / 100) + 0.25 * (energy / 100);
  switch (state.burnout.stage) {
    case 'overextended':
      cap *= 0.97;
      break;
    case 'exhausted':
      cap *= 0.88;
      break;
    case 'atRisk':
      cap *= 0.8;
      break;
    case 'burntOut':
      cap *= 0.52;
      break;
    case 'recovering':
      cap *= 0.7;
      break;
    case 'rebuilding':
      cap *= 0.88;
      break;
    default:
      break;
  }
  for (const c of state.conditions) cap *= 1 - Math.min(0.4, c.severity / 260);
  if (state.education.enrollment) cap *= state.education.enrollment.workCapacity;
  const kids = children(state).filter((c) => c.age < 6).length;
  cap *= 1 - Math.min(0.18, kids * 0.07);
  if (state.business.active) cap *= 1 - Math.min(0.45, state.business.timeDemand / 240);
  return clamp(cap, 0.15, 1.25);
}

/** Total time pressure across work, business, study and family, 0–100. */
export function lifeLoad(state: PlayerState): number {
  const working =
    state.career.status === 'employed' ||
    state.career.status === 'selfEmployed' ||
    state.career.status === 'founder';
  let load = working ? state.career.workload * 0.8 : 0;
  if (state.business.active) load += state.business.timeDemand * 0.5;
  if (state.education.enrollment) load += (1 - state.education.enrollment.workCapacity) * 42;
  const youngKids = children(state).filter((c) => c.age < 12).length;
  load += Math.min(26, youngKids * 11);
  for (const p of state.people) {
    if (p.alive && p.kind === 'parent' && (p.meta.needsCare as boolean)) load += 10;
  }
  return clamp100(load);
}

/* ------------------------------------------------------------------ *
 * Wellbeing composites
 * ------------------------------------------------------------------ */

/** Financial security 0–100: runway, net worth and debt combined. */
export function financialSecurity(state: PlayerState): number {
  const runway = ramp(runwayMonths(state), 0, 12) * 46;
  const nw = netWorth(state);
  const wealth = ramp(Math.log10(Math.max(1, nw)), 3.4, 6.3) * 34;
  const debtDrag = (debtPressure(state) / 100) * 30;
  const flow = netCashFlow(state) >= 0 ? 20 : 0;
  return clamp100(runway + wealth + flow - debtDrag);
}

export function activeRegretWeight(state: PlayerState): number {
  let total = 0;
  for (const r of state.regrets) if (!r.resolved) total += r.weight;
  return total;
}

/* ------------------------------------------------------------------ *
 * Flattened query view used by the data-driven condition evaluator
 * ------------------------------------------------------------------ */

export function queryNumber(state: PlayerState, path: string): number | undefined {
  switch (path) {
    case 'age':
      return state.age;
    case 'health':
    case 'energy':
    case 'stress':
    case 'happiness':
    case 'purpose':
      return state.stats[path];
    case 'cash':
      return state.finance.cash;
    case 'salary':
      return state.finance.salary;
    case 'partnerIncome':
      return partnerIncome(state);
    case 'income':
      return grossIncome(state);
    case 'expenses':
      return annualOutgoings(state);
    case 'cashFlow':
      return netCashFlow(state);
    case 'tax':
      return annualTax(state);
    case 'disposableIncome':
      return disposableIncome(state);
    case 'netWorth':
      return netWorth(state);
    case 'debt':
      return totalDebt(state);
    case 'unsecuredDebt':
      return unsecuredDebt(state);
    case 'debtPressure':
      return debtPressure(state);
    case 'investments':
      return investableValue(state);
    case 'retirementAssets':
      return state.finance.retirementAssets;
    case 'runwayMonths':
      return runwayMonths(state);
    case 'lifestyle':
      return state.finance.lifestyleLevel;
    case 'workload':
      return state.career.workload;
    case 'performance':
      return state.career.performance;
    case 'jobSecurity':
      return state.career.jobSecurity;
    case 'meaningAlignment':
      return state.career.meaningAlignment;
    case 'careerReputation':
      return state.career.reputation;
    case 'roleLevel':
      return state.career.roleLevel;
    case 'yearsExperience':
      return state.career.yearsExperience;
    case 'burnoutLoad':
      return state.burnout.load;
    case 'inertiaPressure':
      return state.inertia.pressure;
    case 'intentionCount':
      return state.inertia.intentions.length;
    case 'childCount':
      return children(state).length;
    case 'dependents':
      return state.family.dependents;
    case 'householdHarmony':
      return state.family.householdHarmony;
    case 'socialHealth':
      return socialHealth(state);
    case 'supportNetwork':
      return supportNetwork(state);
    case 'financialSecurity':
      return financialSecurity(state);
    case 'lifeLoad':
      return lifeLoad(state);
    case 'workCapacity':
      return workCapacity(state);
    case 'educationRank':
      return EDUCATION_RANK_LOOKUP[state.education.level];
    case 'businessHealth':
      return state.business.active ? state.business.health : 0;
    case 'businessRevenue':
      return state.business.active ? state.business.revenue : 0;
    case 'businessValuation':
      return state.business.active ? state.business.valuation : 0;
    case 'regretWeight':
      return activeRegretWeight(state);
    case 'conditionCount':
      return state.conditions.length;
    case 'frailty':
      return state.frailty;
    case 'marriages':
      return state.family.marriages;
    case 'divorces':
      return state.family.divorces;
    case 'layoffs':
      return state.career.layoffs;
    case 'pivots':
      return state.career.pivots;
    case 'promotions':
      return state.career.promotions;
    case 'friendCount':
      return peopleOfKind(state, 'friend').length;
    default:
      break;
  }
  if (path.startsWith('counter.')) return state.counters[path.slice(8)] ?? 0;
  if (path.startsWith('skill.')) {
    const key = path.slice(6) as keyof typeof state.skills;
    return state.skills[key];
  }
  if (path.startsWith('trait.')) {
    const key = path.slice(6) as keyof typeof state.traits;
    return state.traits[key];
  }
  if (path.startsWith('habit.')) {
    const key = path.slice(6) as keyof typeof state.habits;
    return state.habits[key];
  }
  if (path.startsWith('holding.')) {
    const key = path.slice(8) as AssetClassId;
    return state.finance.holdings[key]?.value ?? 0;
  }
  return undefined;
}

const EDUCATION_RANK_LOOKUP: Record<string, number> = {
  highschool: 0,
  vocational: 1,
  bachelor: 2,
  master: 3,
  doctorate: 4,
};

export function queryString(state: PlayerState, path: string): string | undefined {
  switch (path) {
    case 'stage':
      return state.stage;
    case 'careerStatus':
      return state.career.status;
    case 'careerField':
      return state.career.fieldId ?? '';
    case 'marital':
      return state.family.maritalStatus;
    case 'burnoutStage':
      return state.burnout.stage;
    case 'educationLevel':
      return state.education.level;
    case 'marketRegime':
      return state.market.regime;
    default:
      return undefined;
  }
}

/** Every numeric path the condition DSL accepts — used by content validation. */
export const NUMERIC_PATHS: readonly string[] = [
  'age', 'health', 'energy', 'stress', 'happiness', 'purpose', 'cash', 'salary', 'partnerIncome', 'income',
  'expenses', 'cashFlow', 'tax', 'disposableIncome', 'netWorth', 'debt', 'debtPressure', 'investments', 'retirementAssets',
  'runwayMonths', 'unsecuredDebt', 'lifestyle', 'workload', 'performance', 'jobSecurity', 'meaningAlignment',
  'careerReputation', 'roleLevel', 'yearsExperience', 'burnoutLoad', 'inertiaPressure',
  'intentionCount', 'childCount', 'dependents', 'householdHarmony', 'socialHealth',
  'supportNetwork', 'financialSecurity', 'lifeLoad', 'workCapacity', 'educationRank',
  'businessHealth', 'businessRevenue', 'businessValuation', 'regretWeight', 'conditionCount',
  'frailty', 'marriages', 'divorces', 'layoffs', 'pivots', 'promotions', 'friendCount',
];

/**
 * Prefixed paths are dynamic: `skill.technical`, `counter.crises`, and so on.
 * The validator uses this rather than the flat list so content can address any
 * real key without the list having to enumerate every combination.
 */
export function isValidNumericPath(path: string): boolean {
  if (NUMERIC_PATHS.includes(path)) return true;
  const [prefix, rest] = [path.slice(0, path.indexOf('.')), path.slice(path.indexOf('.') + 1)];
  if (!rest) return false;
  switch (prefix) {
    case 'counter':
      return rest.length > 0;
    case 'skill':
      return (SKILL_KEYS as readonly string[]).includes(rest);
    case 'trait':
      return (TRAIT_KEYS as readonly string[]).includes(rest);
    case 'habit':
      return (HABIT_KEYS as readonly string[]).includes(rest);
    case 'holding':
      return ['cash', 'conservative', 'balanced', 'growth', 'speculative', 'property'].includes(rest);
    default:
      return false;
  }
}

export const STRING_PATHS: readonly string[] = [
  'stage', 'careerStatus', 'careerField', 'marital', 'burnoutStage', 'educationLevel', 'marketRegime',
];
